import { useState, useEffect, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, Save } from "lucide-react";
import { RichTextEditor } from "@/components/editor/RichTextEditor";

export function DocumentRichTextAuthor({
  documentId,
  content,
  onSave,
  saving,
  hint,
  placeholder,
  extraActions,
}: {
  /** Resets the local draft when a different document is selected. */
  documentId: string;
  content: string | null;
  onSave: (markdown: string) => void;
  saving: boolean;
  hint: string;
  placeholder: string;
  /** Rendered next to "Salvar rascunho" — e.g. a "generate from items" button. Receives a setter for the draft. */
  extraActions?: (setDraft: (markdown: string) => void, draft: string) => ReactNode;
}) {
  const [draft, setDraft] = useState(content ?? "");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setDraft(content ?? "");
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);

  // The saved value catching up with the draft (after the mutation refetches) is the "save succeeded" signal.
  useEffect(() => {
    if ((content ?? "") === draft) setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  const replaceDraft = (markdown: string) => {
    setDraft(markdown);
    setDirty(true);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="min-w-[240px] flex-1 text-xs text-muted-foreground">{hint}</p>
        <div className="flex items-center gap-2">
          {extraActions?.(replaceDraft, draft)}
          <Button size="sm" onClick={() => onSave(draft)} disabled={saving || !dirty}>
            {saving ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-1.5 h-4 w-4" />
            )}
            Salvar rascunho
          </Button>
        </div>
      </div>
      <RichTextEditor content={draft} onChange={replaceDraft} placeholder={placeholder} />
    </div>
  );
}
